/*
  NEO Sabers — Offer popup (pick your free products)
  Used by sections/neo-offer-popup.liquid (loaded by the section itself).

  - Watches every add-to-cart (fetch to /cart/add). If an added product is in the
    popup's trigger collection, the popup opens.
  - "Add free" adds that variant at once with property `_neo_offer: <Offer name>`.
  - Allowed picks = max (or max × qualifying quantity).
  - Guard: when qualifying products are removed/reduced, extra free lines are removed.
*/
(() => {
  if (window.__neoOfferPopup) return;
  window.__neoOfferPopup = true;

  const PROP = '_neo_offer';
  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);
  let offers = [];
  let guardBusy = false;
  let guardTimer;

  /* ---------------- helpers ---------------- */
  const api = async (path, body) => {
    const res = await nativeFetch(ROOT + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.description || json.message || 'Something went wrong. Try again.');
    return json;
  };
  const getCart = () => api('cart.js');

  const refreshDrawer = async () => {
    const cart = await getCart();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } })
    );
    return cart;
  };

  const isGift = (item, o) => item.properties && item.properties[PROP] === o.cfg.offer;
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  const counts = (cart, o) => {
    let qual = 0, gifts = 0;
    const byVariant = {};
    cart.items.forEach((i) => {
      if (isGift(i, o)) {
        gifts += i.quantity;
        byVariant[i.variant_id] = (byVariant[i.variant_id] || 0) + i.quantity;
      } else if (o.ids && o.ids.has(i.product_id)) {
        qual += i.quantity;
      }
    });
    const allowed = qual > 0 ? (o.cfg.perQty ? o.cfg.max * qual : o.cfg.max) : 0;
    return { qual, gifts, allowed, byVariant };
  };

  /* ---------------- trigger collection ids ---------------- */
  const loadIds = async (o) => {
    if (o.ids) return o.ids;
    const key = 'neoOpIds:' + o.cfg.collection;
    try {
      const c = JSON.parse(sessionStorage.getItem(key));
      if (c && Date.now() - c.t < 30 * 60 * 1000) return (o.ids = new Set(c.ids));
    } catch (e) {}
    const res = await nativeFetch(`${ROOT}collections/${o.cfg.collection}/products.json?limit=250`);
    const json = await res.json();
    o.ids = new Set((json.products || []).map((p) => p.id));
    try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), ids: [...o.ids] })); } catch (e) {}
    return o.ids;
  };

  /* ---------------- UI ---------------- */
  const render = (o) => {
    const { el, cfg, state } = o;
    const remaining = Math.max(0, state.allowed - state.picked);
    const full = state.allowed > 0 && remaining === 0;

    el.classList.toggle('is-full', full);

    // progress steps
    const stepsWrap = el.querySelector('[data-neo-op-steps]');
    const stepCount = Math.min(Math.max(state.allowed, 1), 12);
    if (stepsWrap.children.length !== stepCount) {
      stepsWrap.innerHTML = '<span class="neo-op__step"></span>'.repeat(stepCount);
    }
    [...stepsWrap.children].forEach((s, i) => s.classList.toggle('is-on', i < state.picked));
    el.querySelector('[data-neo-op-picked]').textContent = state.picked;
    el.querySelector('[data-neo-op-allowed]').textContent = state.allowed;

    // cards
    el.querySelectorAll('[data-neo-op-card]').forEach((card) => {
      const id = Number(card.getAttribute('data-neo-op-card'));
      const btn = card.querySelector('[data-neo-op-add]');
      const qty = state.byVariant[id] || 0;
      const soldout = btn.hasAttribute('data-soldout');

      card.classList.toggle('is-added', qty > 0);
      card.classList.toggle('is-disabled', !soldout && full && qty === 0);
      card.querySelector('[data-neo-op-qty]').textContent = qty > 1 ? `✓ ×${qty}` : '✓ Added';

      if (soldout) return;
      btn.disabled = full && qty === 0;
      btn.textContent = qty > 0 ? (full ? '✓ Added' : 'Add another') : full ? 'Limit reached' : 'Add free';
      if (full && qty > 0) btn.disabled = true;
    });

    // footer
    const hint = el.querySelector('[data-neo-op-hint]');
    const done = el.querySelector('[data-neo-op-done]');
    const needMore = Math.max(0, cfg.min - state.picked);
    hint.classList.remove('is-error');

    if (needMore > 0) hint.textContent = `Select ${plural(needMore, cfg.unit)} more`;
    else if (remaining > 0) hint.textContent = `You can add ${plural(remaining, cfg.unit)} more`;
    else hint.textContent = `All ${plural(state.allowed, cfg.unit)} added to your cart`;

    done.textContent = needMore > 0 ? `Select ${needMore} more` : cfg.doneLabel;
    done.classList.toggle('is-muted', needMore > 0);

    el.classList.toggle('is-locked', !cfg.allowClose && needMore > 0);
  };

  const canClose = (o) => o.cfg.allowClose || o.state.picked >= o.cfg.min;

  const open = (o, state) => {
    offers.forEach((x) => x !== o && x.el.classList.contains('is-open') && close(x, true));
    o.state = state;
    render(o);
    o.el.classList.add('is-open');
    o.el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('neo-op-lock');
    setTimeout(() => {
      const first = o.el.querySelector('[data-neo-op-add]:not([disabled]):not([data-soldout])');
      first && first.focus({ preventScroll: true });
    }, 300);
  };

  const close = (o, force) => {
    if (!force && !canClose(o)) {
      const hint = o.el.querySelector('[data-neo-op-hint]');
      hint.classList.remove('is-shake');
      void hint.offsetWidth;
      hint.classList.add('is-shake');
      return;
    }
    clearTimeout(o.autoClose);
    o.el.classList.remove('is-open');
    o.el.setAttribute('aria-hidden', 'true');
    if (!offers.some((x) => x.el.classList.contains('is-open'))) {
      document.documentElement.classList.remove('neo-op-lock');
    }
  };

  const openFromCart = async (o, cart) => {
    cart = cart || (await getCart());
    const c = counts(cart, o);
    if (c.allowed - c.gifts <= 0) return false;
    open(o, { allowed: c.allowed, picked: c.gifts, byVariant: c.byVariant });
    return true;
  };

  const addGift = async (o, variantId, btn) => {
    const s = o.state;
    if (o.busy || s.allowed - s.picked <= 0) return;
    o.busy = true;
    btn.classList.add('is-loading');
    const label = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      await api('cart/add.js', {
        items: [{ id: variantId, quantity: 1, properties: { [PROP]: o.cfg.offer } }]
      });
      s.picked += 1;
      s.byVariant[variantId] = (s.byVariant[variantId] || 0) + 1;
      render(o);
      refreshDrawer().catch(() => {});
      if (s.allowed - s.picked <= 0 && s.picked >= o.cfg.min) {
        o.autoClose = setTimeout(() => close(o, true), 1400);
      }
    } catch (err) {
      btn.textContent = label;
      const hint = o.el.querySelector('[data-neo-op-hint]');
      hint.textContent = err.message;
      hint.classList.add('is-error');
    } finally {
      btn.classList.remove('is-loading');
      o.busy = false;
    }
  };

  /* ---------------- add-to-cart detection ---------------- */
  const onAdded = async (data) => {
    const items = data && data.items ? data.items : data && data.product_id ? [data] : [];
    if (!items.length) return;

    for (const o of offers) {
      try {
        await loadIds(o);
        const hit = items.some((i) => o.ids.has(i.product_id) && !isGift(i, o));
        if (!hit) continue;
        await new Promise((r) => setTimeout(r, 350)); // let the theme drawer open first
        if (await openFromCart(o)) break;
      } catch (e) {
        console.warn('[neo-offer-popup]', e);
      }
    }
  };

  window.fetch = function (input, init) {
    const url = input instanceof Request ? input.url : String(input);
    const promise = nativeFetch(input, init);
    if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
      promise
        .then((res) => {
          if (res.ok) res.clone().json().then(onAdded).catch(() => {});
        })
        .catch(() => {});
    }
    return promise;
  };

  /* ---------------- guard: trim extra free lines ---------------- */
  const guard = async () => {
    if (guardBusy || !offers.length) return;
    guardBusy = true;
    try {
      const cart = await getCart();
      const updates = {};

      for (const o of offers) {
        if (!o.ids) continue;
        const c = counts(cart, o);
        let excess = c.gifts - c.allowed;
        if (excess <= 0) continue;

        const lines = cart.items.filter((i) => isGift(i, o)).reverse();
        for (const line of lines) {
          if (excess <= 0) break;
          const take = Math.min(line.quantity, excess);
          updates[line.key] = line.quantity - take;
          excess -= take;
        }
      }

      if (Object.keys(updates).length) {
        await api('cart/update.js', { updates });
        await refreshDrawer();
      }
    } catch (e) {
      console.warn('[neo-offer-popup]', e);
    } finally {
      setTimeout(() => (guardBusy = false), 400);
    }
  };

  const queueGuard = () => {
    clearTimeout(guardTimer);
    guardTimer = setTimeout(guard, 300);
  };

  /* ---------------- init ---------------- */
  const init = () => {
    offers = [...document.querySelectorAll('[data-neo-offer-popup]')]
      .map((el) => {
        try {
          const cfg = JSON.parse(el.querySelector('[data-neo-op-config]').textContent);
          return { el, cfg, ids: null, state: { allowed: cfg.max, picked: 0, byVariant: {} } };
        } catch (e) {
          return null;
        }
      })
      .filter(Boolean);

    offers.forEach((o) => {
      // move to <body> so drawer/section stacking never hides it
      if (o.el.parentElement !== document.body) document.body.appendChild(o.el);
      loadIds(o).then(queueGuard).catch(() => {});
    });
  };

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t.closest) return;
    const root = t.closest('[data-neo-offer-popup]');
    if (!root) return;
    const o = offers.find((x) => x.el === root);
    if (!o) return;

    const add = t.closest('[data-neo-op-add]');
    if (add && !add.disabled && !add.hasAttribute('data-soldout')) {
      e.preventDefault();
      addGift(o, Number(add.getAttribute('data-neo-op-add')), add);
      return;
    }
    if (t.closest('[data-neo-op-close]') || t.closest('[data-neo-op-overlay]') || t.closest('[data-neo-op-done]')) {
      e.preventDefault();
      close(o);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    offers.forEach((o) => o.el.classList.contains('is-open') && close(o));
  });

  ['cart:change', 'cart:refresh'].forEach((ev) => document.addEventListener(ev, queueGuard));

  const drawer = () => document.getElementById('cart-drawer');
  const observe = () => {
    const d = drawer();
    if (d) new MutationObserver(queueGuard).observe(d, { childList: true, subtree: true });
  };

  /* ---------------- theme editor preview ---------------- */
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('body > [data-neo-offer-popup]').forEach((el) => {
      if (!document.contains(el)) return;
      const id = el.getAttribute('data-section-id');
      const fresh = document.querySelectorAll(`[data-neo-offer-popup][data-section-id="${id}"]`);
      if (fresh.length > 1) el.remove();
    });
    init();
  });
  document.addEventListener('shopify:section:select', (e) => {
    const o = offers.find((x) => x.el.getAttribute('data-section-id') === e.detail.sectionId);
    if (o) open(o, { allowed: o.cfg.max, picked: 0, byVariant: {} });
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const o = offers.find((x) => x.el.getAttribute('data-section-id') === e.detail.sectionId);
    if (o) close(o, true);
  });
  document.addEventListener('shopify:block:select', (e) => {
    const o = offers.find((x) => x.el.getAttribute('data-section-id') === e.detail.sectionId);
    if (o && !o.el.classList.contains('is-open')) open(o, { allowed: o.cfg.max, picked: 0, byVariant: {} });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); observe(); });
  } else {
    init();
    observe();
  }
})();
